/**
 * LiveStatsBlock: `live-stats` DYNAMIC content block.
 *
 * data: { sectionHeading?, visibleStats? }
 *
 * Fetches live platform statistics on mount and renders the stats named in
 * `visibleStats` (an ordered array) as a row of callout cards. Each source
 * fails silently: if an API call errors the stats derived from that source
 * are omitted rather than crashing the block. The block renders null until
 * at least one stat is available.
 *
 * Data sources:
 *   graph   — GET /api/graph/stats         (edgeCount, avgDegree)
 *   dist    — GET /api/organizations/stats (totalOrganizations, sectorCount, regionCount)
 *   mentors — GET /api/users/mentors/count (count)
 *   users   — GET /api/users/count         (count)
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row } from 'react-bootstrap';

import { useGraphApi } from '@/shared/services/graphApi';
import useOrganizationApi from '@/shared/services/organizationApi';
import { useUserApi } from '@/shared/services/userApi';
import useIsFlagEnabled from '@/shared/hooks/useIsFlagEnabled';

const STAT_CONFIG = {
    organizations: {
        label: 'Organizations',
        getValue: (g, d) => d?.totalOrganizations,
    },
    connections: {
        label: 'Connections',
        getValue: (g) => g?.edgeCount,
    },
    sectors: {
        label: 'Active Sectors',
        getValue: (_g, d) => d?.sectorCount,
    },
    regions: {
        label: 'Regions Covered',
        getValue: (_g, d) => d?.regionCount,
    },
    avgDegree: {
        label: 'Avg. Connections per Org',
        getValue: (g) =>
            g?.avgDegree != null
                ? parseFloat(g.avgDegree.toFixed(1))
                : undefined,
    },
    mentors: {
        label: 'Mentors Available',
        getValue: (_g, _d, m) => m?.count,
    },
    users: {
        label: 'Ecosystem Members',
        getValue: (_g, _d, _m, u) => u?.count,
    },
};

export default function LiveStatsBlock({ data }) {
    const d = data || {};
    const sectionHeading = d.sectionHeading;
    const visibleStats =
        Array.isArray(d.visibleStats) && d.visibleStats.length > 0
            ? d.visibleStats
            : ['organizations', 'connections'];

    const isFlagEnabled = useIsFlagEnabled();
    const mentoringEnabled = isFlagEnabled('mentoring');

    const graphApi = useGraphApi();
    const orgApi = useOrganizationApi();
    const userApi = useUserApi();

    const graphApiRef = useRef(graphApi);
    const orgApiRef = useRef(orgApi);
    const userApiRef = useRef(userApi);
    const mentoringEnabledRef = useRef(mentoringEnabled);
    graphApiRef.current = graphApi;
    orgApiRef.current = orgApi;
    userApiRef.current = userApi;
    mentoringEnabledRef.current = mentoringEnabled;

    const [graphStats, setGraphStats] = useState(null);
    const [distStats, setDistStats] = useState(null);
    const [mentorData, setMentorData] = useState(null);
    const [memberData, setMemberData] = useState(null);

    useEffect(() => {
        let cancelled = false;

        Promise.allSettled([
            graphApiRef.current
                .fetchStats()
                .then((r) => {
                    if (!cancelled) setGraphStats(r);
                })
                .catch(() => {}),
            orgApiRef.current
                .fetchDistributionStats()
                .then((r) => {
                    if (!cancelled) setDistStats(r);
                })
                .catch(() => {}),
            ...(mentoringEnabledRef.current
                ? [
                      userApiRef.current
                          .fetchMentorCount()
                          .then((r) => {
                              if (!cancelled) setMentorData(r);
                          })
                          .catch(() => {}),
                  ]
                : []),
            userApiRef.current
                .fetchMemberCount()
                .then((r) => {
                    if (!cancelled) setMemberData(r);
                })
                .catch(() => {}),
        ]);

        return () => {
            cancelled = true;
        };
    }, []);

    const statItems = visibleStats
        .filter((key) => key !== 'mentors' || mentoringEnabled)
        .map((key) => {
            const config = STAT_CONFIG[key];
            if (!config) return null;
            const value = config.getValue(
                graphStats,
                distStats,
                mentorData,
                memberData
            );
            if (value == null) return null;
            return { key, label: config.label, value };
        })
        .filter(Boolean);

    if (statItems.length === 0) return null;

    const colCount = Math.min(statItems.length, 3);

    return (
        <>
            {sectionHeading && (
                <h2 className="content-heading--h2 text-center text-primary mb-4">
                    {sectionHeading}
                </h2>
            )}
            <Row
                xs={Math.min(statItems.length, 2)}
                md={colCount}
                className="g-3 justify-content-center"
            >
                {statItems.map(({ key, label, value }) => (
                    <Col key={key}>
                        <Card className="border-0 shadow-soft text-center h-100">
                            <Card.Body>
                                <div className="content-stat__value fw-bold text-primary">
                                    {typeof value === 'number'
                                        ? value.toLocaleString()
                                        : value}
                                </div>
                                <div className="text-muted small">{label}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>
        </>
    );
}

LiveStatsBlock.propTypes = {
    data: PropTypes.shape({
        sectionHeading: PropTypes.string,
        visibleStats: PropTypes.arrayOf(PropTypes.string),
    }),
};

LiveStatsBlock.defaultProps = {
    data: {},
};
